import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Tooltip,
  useMediaQuery,
  useTheme,
  Typography,
  Paper,
  List,
  ListItemButton,
  ClickAwayListener,
  Chip,
  Alert,
} from "@mui/material";
import { InsertEmoticon, Mic, Send, Close, InfoOutlined, AutoAwesome } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import ChannelSelector from "./ChannelSelector";
import ComposeAttachMenu from "./ComposeAttachMenu";
import { useComposeSelectionFormat } from "../../hooks/useComposeSelectionFormat";
import { getChannelById, getChannelComposerSurfaceSx } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";
import { composerHasSendPayload } from "./composeAttachmentHelpers";
import { COMPOSER_ROW_HEIGHT_PX } from "./composerConstants";
import RecordingIndicator from "./RecordingIndicator";
import AudioPreview from "./AudioPreview";

const SUGGESTIONS_MAX_HEIGHT = 280;
const SUGGESTION_PREVIEW_LENGTH = 120;

/** Normaliza string para busca ignorando acentos (lowercase, sem diacríticos). */
function normalizeForSearch(s) {
  if (s == null || typeof s !== "string") return "";
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

const MessageWhatsappChannel = ({
  message,
  setMessage,
  channelType,
  selectedChannel,
  setSelectedChannel,
  formatOptions,
  startRecording,
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  file,
  files = [],
  audioURL,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
  suggestions = { templates: [], quickReplies: [] },
  isLoadingSuggestions = false,
  suggestionsError = null,
  requiresTemplate = false,
  selectedTemplate = null,
  onTemplateSelect,
  externalPickerOpen = false,
  sendBlockedMissingPhone = false,
  sendComposeBlocked = false,
  onSendCurrentLocation,
  locationSending = false,
  locationSendDisabled = false,
  locationSendDisabledHelp = null,
  onOpenProductPicker,
  isPersonalWhatsappThread = false,
  isRecording = false,
  recordingDuration = 0,
  stopRecording = () => {},
  discardRecording = () => {},
  onRemoveAudio,
}) => {
  const { t } = useTranslation("chat");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const [windowAlertDismissed, setWindowAlertDismissed] = useState(false);
  const [manualTemplatesOpen, setManualTemplatesOpen] = useState(false);
  const inputRef = useRef(null);
  const anchorRef = useRef(null);
  const highlightedItemRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const theme = useTheme();
  const inputBorderRadius = 16; // consistente com o campo do chat
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const currentChannel = getChannelById(channels, selectedChannel);
  const channelName = currentChannel?.nome || currentChannel?.name || null;
  const composerSurface = getChannelComposerSurfaceSx(theme, {
    channelType,
    channelName,
    isInternalNote: false,
    isPersonalWhatsapp: isPersonalWhatsappThread,
  });
  const showAiButton = !!onGenerateSuggestion && channelType !== "Xpro";
  const aiDisabled = isGeneratingSuggestion || !hasHistory;
  const hasTemplatesAvailable = (suggestions?.templates || []).length > 0;
  const canSend = composerHasSendPayload(message, file, files, audioURL);

  const { composeInputHandlers, selectionToolbar } = useComposeSelectionFormat({
    inputRef,
    setMessage,
    channelType,
    formatOptions,
  });

  const handleOpenProductPickerFromMenu = useCallback(() => {
    onOpenProductPicker?.();
    queueMicrotask(() => inputRef.current?.focus());
  }, [onOpenProductPicker]);

  const combinedSuggestions = useMemo(() => {
    const list = [];
    (suggestions?.templates || []).forEach((tpl) => {
      const fullText = (tpl.fullText || tpl.preview || tpl.name || "").trim();
      const displayText = (tpl.preview || tpl.name || fullText || "").trim();
      if (displayText) {
        list.push({
          type: "template",
          id: tpl.id,
          name: tpl.name,
          displayText,
          fullText,
          raw: tpl,
        });
      }
    });
    (suggestions?.quickReplies || []).forEach((qr) => {
      const displayText = (qr.text || "").trim();
      if (displayText) {
        list.push({
          type: "quickReply",
          id: qr.id,
          name: qr.text,
          displayText,
          fullText: displayText,
          raw: qr,
        });
      }
    });
    return list;
  }, [suggestions?.templates, suggestions?.quickReplies]);

  const searchTrim = (message || "").trim();
  const filteredSuggestions = useMemo(() => {
    if (searchTrim.length === 0) return [];
    const normSearch = normalizeForSearch(searchTrim);
    return combinedSuggestions.filter(
      (item) =>
        (item.displayText && normalizeForSearch(item.displayText).includes(normSearch)) ||
        (item.name && normalizeForSearch(String(item.name)).includes(normSearch))
    );
  }, [combinedSuggestions, searchTrim]);

  const suggestionsList = useMemo(() => {
    // Fora da janela de 24h/primeiro contato, o operador precisa escolher um template.
    // Nesse caso, quando o campo estiver vazio, mostramos a lista completa de templates
    // (sem depender de match do texto), e filtramos conforme o usuário digita.
    if (requiresTemplate && hasTemplatesAvailable) {
      if (searchTrim.length === 0) {
        return (combinedSuggestions || []).filter((x) => x.type === "template");
      }
      // Se digitou algo mas não encontrou match, ainda assim mantém a lista de templates
      // para permitir seleção (a digitação pode ser uma saudação, etc.).
      return filteredSuggestions.length > 0 ? filteredSuggestions : (combinedSuggestions || []).filter((x) => x.type === "template");
    }
    return filteredSuggestions;
  }, [
    combinedSuggestions,
    filteredSuggestions,
    hasTemplatesAvailable,
    requiresTemplate,
    searchTrim.length,
  ]);

  const templatesOnlyList = useMemo(
    () => (combinedSuggestions || []).filter((x) => x.type === "template"),
    [combinedSuggestions]
  );

  const shouldShowTemplatesWhileEmpty =
    requiresTemplate && hasTemplatesAvailable && manualTemplatesOpen && searchTrim.length === 0;

  const suggestionsOpen =
    !externalPickerOpen &&
    !suggestionsDismissed &&
    (searchTrim.length >= 1 ? suggestionsList.length > 0 : shouldShowTemplatesWhileEmpty);

  const openTemplatesManually = () => {
    if (!requiresTemplate || !hasTemplatesAvailable) return;
    if (externalPickerOpen) return;
    setManualTemplatesOpen(true);
    setSuggestionsDismissed(false);
    setHighlightedIndex(0);
    queueMicrotask(() => inputRef.current?.focus());
  };

  // Se abriu picker externo (@/#), fecha templates para não conflitar
  useEffect(() => {
    if (!externalPickerOpen) return;
    setManualTemplatesOpen(false);
    setSuggestionsDismissed(true);
  }, [externalPickerOpen]);

  useEffect(() => {
    if (!suggestionsOpen) setHighlightedIndex(0);
    else setHighlightedIndex((i) => Math.min(i, suggestionsList.length - 1));
  }, [suggestionsOpen, suggestionsList.length]);

  // Rolagem para manter o item destacado visível ao navegar com setas
  useEffect(() => {
    if (!suggestionsOpen || highlightedIndex < 0) return;
    highlightedItemRef.current?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
      inline: "nearest",
    });
  }, [suggestionsOpen, highlightedIndex]);

  const closeSuggestions = () => {
    setHighlightedIndex(0);
    setSuggestionsDismissed(true);
    setManualTemplatesOpen(false);
  };

  const selectSuggestion = (item) => {
    const textToUse =
      item.type === "template"
        ? (item.fullText || item.displayText)
        : item.displayText;

    setMessage(textToUse);

    if (item.type === "template" && typeof onTemplateSelect === "function") {
      onTemplateSelect({
        id: item.id,
        name: item.name,
        preview: item.displayText,
        raw: item.raw,
      });
    } else if (typeof onTemplateSelect === "function") {
      onTemplateSelect(null);
    }

    setSuggestionsDismissed(true);
    setHighlightedIndex(0);
    if (inputRef.current) inputRef.current.focus();
  };

  useEffect(() => {
    if (searchTrim.length === 0) {
      // não abre automaticamente; apenas reabilita caso o usuário digite novamente
      setSuggestionsDismissed(false);
    }
  }, [searchTrim.length]);

  const handleInputKeyDown = (e) => {
    if (suggestionsOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((i) => (i < filteredSuggestions.length - 1 ? i + 1 : 0));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((i) => (i > 0 ? i - 1 : filteredSuggestions.length - 1));
        return;
      }
      if (e.key === "Enter" && suggestionsList[highlightedIndex]) {
        e.preventDefault();
        selectSuggestion(suggestionsList[highlightedIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeSuggestions();
        return;
      }
    }
    // Atalho: Ctrl+Espaço abre templates quando obrigatório
    if (
      (e.ctrlKey || e.metaKey) &&
      e.code === "Space" &&
      requiresTemplate &&
      hasTemplatesAvailable &&
      !externalPickerOpen
    ) {
      e.preventDefault();
      openTemplatesManually();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (sendBlockedMissingPhone || sendComposeBlocked) return;
      handleSendMessage();
    }
  };

  const previewText = (text) => {
    if (!text) return "";
    const s = String(text).trim().replace(/\s+/g, " ");
    if (s.length <= SUGGESTION_PREVIEW_LENGTH) return s;
    return `${s.slice(0, SUGGESTION_PREVIEW_LENGTH)}…`;
  };

  /** Encontra trechos em `text` que correspondem a `search` ignorando acentos e retorna partes para destacar em negrito. */
  const highlightMatch = (text, search) => {
    if (!text || !search || !String(search).trim()) return text;
    const str = String(text);
    const term = String(search).trim();
    if (!term) return str;
    const normSearch = normalizeForSearch(term);
    if (!normSearch) return str;
    const chars = Array.from(str);
    let norm = "";
    const normToOrig = [];
    for (let i = 0; i < chars.length; i++) {
      const n = normalizeForSearch(chars[i]);
      if (n.length > 0) {
        for (let j = 0; j < n.length; j++) {
          normToOrig.push({ start: i, end: i + 1 });
          norm += n[j];
        }
      }
    }
    const spans = [];
    let idx = 0;
    while (idx < norm.length) {
      const found = norm.indexOf(normSearch, idx);
      if (found === -1) break;
      const end = found + normSearch.length;
      const oStart = normToOrig[found].start;
      const oEnd = normToOrig[end - 1].end;
      spans.push({ start: oStart, end: oEnd });
      idx = end;
    }
    if (spans.length === 0) return str;
    const parts = [];
    let last = 0;
    spans.forEach(({ start, end }) => {
      if (start > last) {
        parts.push({ key: `t-${last}`, type: "text", value: chars.slice(last, start).join("") });
      }
      parts.push({ key: `m-${start}`, type: "mark", value: chars.slice(start, end).join("") });
      last = end;
    });
    if (last < chars.length) {
      parts.push({ key: `t-${last}`, type: "text", value: chars.slice(last).join("") });
    }
    return parts.map((p) =>
      p.type === "mark" ? (
        <Box
          component="span"
          key={p.key}
          sx={{
            backgroundColor: "grey.200",
            color: "text.primary",
            borderRadius: 0.5,
            px: 0.25,
            fontWeight: 700,
          }}
        >
          {p.value}
        </Box>
      ) : (
        <span key={p.key}>{p.value}</span>
      )
    );
  };

  return (
    <ClickAwayListener onClickAway={closeSuggestions}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? 0.5 : 1,
          paddingBottom: 0,
        }}
      >
        {requiresTemplate && !windowAlertDismissed && (
          <Alert
            severity="info"
            icon={<InfoOutlined sx={{ fontSize: 22 }} />}
            onClose={() => setWindowAlertDismissed(true)}
            sx={{
              // Overlay acima do input para não alterar a altura do composer
              position: "absolute",
              left: 0,
              right: 0,
              bottom: "100%",
              mb: 1,
              py: 1.5,
              px: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "info.light",
              zIndex: 1500,
              "& .MuiAlert-message": { width: "100%", padding: 0 },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: "info.dark" }}>
              {hasHistory ? "Janela de 24 horas do WhatsApp fechada" : "Primeiro contato pelo WhatsApp"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
              {hasHistory ? (
                hasTemplatesAvailable ? (
                  <>
                    Após 24h da última mensagem do cliente, o WhatsApp só permite enviar mensagens através de{" "}
                    <strong>templates de reengajamento</strong>. Digite no campo abaixo para ver os templates disponíveis
                    ou aguarde o cliente enviar uma nova mensagem.
                  </>
                ) : (
                  <>
                    Após 24h da última mensagem do cliente, o WhatsApp só permite enviar mensagens através de{" "}
                    <strong>templates de reengajamento</strong>. Parece que você ainda não possui templates configurados
                    para este canal. Entre em contato com o nosso suporte e solicite o cadastro de templates de
                    reengajamento antes de retomar conversas fora da janela de 24h.
                  </>
                )
              ) : hasTemplatesAvailable ? (
                <>
                  Esta solicitação ainda não tem mensagens pelo WhatsApp. Para iniciar a conversa com o cliente, o
                  WhatsApp exige que a primeira mensagem seja enviada usando um{" "}
                  <strong>template de reengajamento</strong>. Digite no campo abaixo para buscar um template que apresente
                  sua empresa, explique por que você está entrando em contato e oriente claramente qual é o próximo passo
                  para o cliente.
                </>
              ) : (
                <>
                  Esta solicitação ainda não tem mensagens pelo WhatsApp. Para iniciar a conversa com o cliente, o
                  WhatsApp exige que a primeira mensagem seja enviada usando um{" "}
                  <strong>template de reengajamento</strong>. Parece que você ainda não possui templates configurados para
                  este canal: antes de falar com o cliente, entre em contato com o nosso suporte e solicite o cadastro de
                  templates alinhados à sua operação (apresentação, motivo do contato e próximos passos para o cliente).
                </>
              )}
            </Typography>
          </Alert>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: isMobile ? 0.5 : 0,
            width: "100%",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "stretch",
              borderRadius: `${inputBorderRadius}px`,
              minHeight: COMPOSER_ROW_HEIGHT_PX,
              // Importante: a lista de templates abre "para cima" (position:absolute; bottom:100%).
              // Se o container tiver overflow:hidden, a dropdown fica clipada (aparece só a sombra).
              overflow: "visible",
              ...composerSurface.shell,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                alignSelf: "stretch",
                px: 0.25,
                ...composerSurface.divider,
              }}
            >
              <ChannelSelector
                integrated
                selectedChannel={selectedChannel}
                setSelectedChannel={setSelectedChannel}
                personalWhatsappComposerOnly={isPersonalWhatsappThread}
              />
            </Box>
            <Box
              ref={anchorRef}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: `${COMPOSER_ROW_HEIGHT_PX}px`,
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
            {suggestionsOpen && !isRecording && !audioURL && (
              <Paper
                elevation={8}
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: "100%",
                  mb: 0.5,
                  maxHeight: SUGGESTIONS_MAX_HEIGHT,
                  overflow: "hidden",
                  // garantir acima de overlays/portals de menções/produtos
                  zIndex: 1400,
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", maxHeight: SUGGESTIONS_MAX_HEIGHT }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      py: 1,
                      pr: 1,
                      pl: 2,
                      flexShrink: 0,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={closeSuggestions}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "text.primary", backgroundColor: "action.hover" },
                      }}
                      aria-label="Fechar sugestões"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                  <List dense disablePadding sx={{ overflow: "auto", flex: 1, minHeight: 0 }}>
                    {(searchTrim.length === 0 && shouldShowTemplatesWhileEmpty ? templatesOnlyList : suggestionsList).map(
                      (item, idx) => (
                      <ListItemButton
                        key={`${item.type}-${item.id}-${idx}`}
                        ref={idx === highlightedIndex ? highlightedItemRef : null}
                        selected={idx === highlightedIndex}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                        onClick={() => selectSuggestion(item)}
                        sx={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          py: 1,
                          borderBottom:
                            idx <
                            (searchTrim.length === 0 && shouldShowTemplatesWhileEmpty
                              ? templatesOnlyList.length
                              : suggestionsList.length) -
                              1
                              ? "1px solid"
                              : "none",
                          borderColor: "divider",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
                          <Chip
                            size="small"
                            label={item.type === "template" ? "Template" : "Mensagem rápida"}
                            variant="outlined"
                            sx={{
                              flexShrink: 0,
                              height: 20,
                              fontSize: "0.7rem",
                              ...(item.type === "template"
                                ? { borderColor: "primary.main", color: "primary.main" }
                                : { borderColor: "secondary.main", color: "secondary.main" }),
                            }}
                          />
                          <Typography variant="body2" component="span" color="text.primary" sx={{ flex: 1 }}>
                            {highlightMatch(previewText(item.displayText), searchTrim)}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              </Paper>
            )}
            {isRecording ? (
              <RecordingIndicator
                embedded
                durationSeconds={recordingDuration}
                onStop={stopRecording}
                onDiscard={discardRecording}
              />
            ) : audioURL ? (
              <AudioPreview embedded audioURL={audioURL} onRemove={onRemoveAudio} />
            ) : (
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              variant="outlined"
              placeholder={
                requiresTemplate
                  ? hasTemplatesAvailable
                    ? "Use um template de reengajamento (digite para buscar)"
                    : "Você ainda não possui templates de reengajamento cadastrados. Fale com o suporte para configurá-los."
                  : channelType === "Xpro"
                  ? "Registrar uma anotação"
                  : isPersonalWhatsappThread
                  ? t("input.placeholderPersonalWhatsapp")
                  : t("input.placeholderChannel", { channel: channelType })
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleInputKeyDown}
              inputProps={composeInputHandlers}
              InputProps={{
                style: {
                  borderRadius: 0,
                  fontSize: isMobile ? "0.95rem" : "1rem",
                },
                inputRef: inputRef,
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>
                    <IconButton
                      onClick={handleEmojiPickerOpen}
                      edge="start"
                      size={isMobile ? "small" : "medium"}
                      aria-label={t("input.emojiAria")}
                    >
                      <InsertEmoticon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ mr: 0.25, display: "flex", alignItems: "center", gap: 0 }}
                  >
                    {showAiButton ? (
                      <Tooltip
                        title={
                          aiDisabled
                            ? isGeneratingSuggestion
                              ? t("input.aiGenerating")
                              : t("input.aiNoHistory")
                            : t("input.aiSuggest")
                        }
                      >
                        <span>
                          <IconButton
                            onClick={onGenerateSuggestion}
                            disabled={aiDisabled}
                            size={isMobile ? "small" : "medium"}
                            aria-label={t("input.aiSuggest")}
                          >
                            <AutoAwesome fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                    <ComposeAttachMenu
                      openFilePicker={openFilePicker}
                      showAttachProducts={typeof onOpenProductPicker === "function"}
                      onOpenProductPicker={handleOpenProductPickerFromMenu}
                      showTemplates={requiresTemplate && hasTemplatesAvailable}
                      onOpenTemplates={openTemplatesManually}
                      templatesDisabled={externalPickerOpen}
                      templatesDisabledHelp="Feche a lista de @/# para ver templates."
                      showLocation={typeof onSendCurrentLocation === "function"}
                      onSendCurrentLocation={onSendCurrentLocation}
                      locationSendDisabled={locationSendDisabled}
                      locationSending={locationSending}
                      locationSendDisabledHelp={locationSendDisabledHelp}
                      size={isMobile ? "small" : "medium"}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                "& .MuiInputBase-root": {
                  minHeight: `${COMPOSER_ROW_HEIGHT_PX}px`,
                  boxSizing: "border-box",
                  borderRadius: "0 !important",
                  overflow: "hidden",
                  clipPath: "none",
                  "&::-webkit-scrollbar": {
                    width: "5px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#b0b0b0",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#888888",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0 !important",
                  overflow: "hidden",
                  clipPath: "none",
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                  borderRadius: "0 !important",
                },
                "& textarea": {
                  overflowY: "auto",
                  resize: "none",
                  boxSizing: "border-box",
                  borderRadius: "inherit",
                  overflowX: "hidden",
                  padding: "4px 10px",
                  "&::-webkit-scrollbar": {
                    width: "5px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#b0b0b0",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#888888",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                  },
                },
              }}
            />
            )}
            </Box>
          </Box>
          {isMobile ? (
            <IconButton
              onClick={() => {
                if (sendBlockedMissingPhone || sendComposeBlocked) return;
                if (canSend) handleSendMessage();
                else startRecording();
              }}
              disabled={sendBlockedMissingPhone || sendComposeBlocked}
              aria-label={
                canSend ? t("input.sendAria") : t("input.recordVoiceAria")
              }
              sx={{
                backgroundColor: isPersonalWhatsappThread ? "#128C7E" : "primary.main",
                color: "primary.contrastText",
                borderRadius: "50%",
                p: 1,
                flexShrink: 0,
                "&:hover": { backgroundColor: isPersonalWhatsappThread ? "#0f6f64" : "primary.dark" },
              }}
            >
              {canSend ? (
                <Send fontSize="small" />
              ) : (
                <Mic fontSize="small" />
              )}
            </IconButton>
          ) : null}
        </Box>
        {selectionToolbar}
      </Box>
    </ClickAwayListener>
  );
};

export default MessageWhatsappChannel;
